import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../../components/ui/accordion';
import {
  GitBranch, Settings, ArrowRight, CheckCircle, Edit, Save, Loader2,
  RefreshCw, Bell, Mail, Building2, Package, HandHeart, MessageSquare,
  FileText, Plus, X, AlertCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';

const ENTIDADE_ICONS = {
  candidaturas: Building2,
  pedidos: Package,
  missoes: HandHeart,
  tickets: MessageSquare
};

const ENTIDADE_LABELS = {
  candidaturas: 'Candidaturas de Instituições',
  pedidos: 'Pedidos de Apoio',
  missoes: 'Missões de Voluntariado',
  tickets: 'Tickets de Suporte'
};

const TIPOS_DOCUMENTO = [
  'Estatutos', 'Regulamento Interno', 'Relatório de Atividades',
  'Plano Atividades', 'Relatório de Contas', 'Balanço',
  'Demonstração de Resultados', 'Certificado de Registo',
  'Acordo de Cooperação', 'Alvará/Licença', 'Seguro',
  'Declaração IRS/IRC', 'Certidão Permanente', 'Comprovativo NIF',
  'Comprovativo IBAN', 'Declaração Utilidade Pública', 'Outro'
];

// Componente auxiliar para adicionar documentos obrigatórios usando Select
function DocObrigatorioInput({ estadoId, onAdd }) {
  const [selectedDoc, setSelectedDoc] = useState('');
  return (
    <div className="flex gap-2 mt-1">
      <Select value={selectedDoc} onValueChange={setSelectedDoc}>
        <SelectTrigger className="h-7 text-xs flex-1">
          <SelectValue placeholder="Selecionar tipo de documento..." />
        </SelectTrigger>
        <SelectContent>
          {TIPOS_DOCUMENTO.map(tipo => (
            <SelectItem key={tipo} value={tipo} className="text-xs">{tipo}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button" size="sm" variant="outline" className="h-7 px-2 shrink-0"
        disabled={!selectedDoc}
        onClick={() => { if (selectedDoc) { onAdd(estadoId, selectedDoc); setSelectedDoc(''); } }}
      >
        <Plus className="w-3 h-3" />
      </Button>
    </div>
  );
}

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedWorkflow, setEditedWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/workflows`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data);
      }
    } catch (error) {
      console.error('Error loading workflows:', error);
      toast.error('Erro ao carregar workflows');
    } finally {
      setLoading(false);
    }
  };

  const openWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setEditedWorkflow(JSON.parse(JSON.stringify(workflow))); // Deep copy
    setEditMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/workflows/${editedWorkflow.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editedWorkflow)
        }
      );
      
      if (response.ok) {
        toast.success('Workflow atualizado com sucesso');
        setSelectedWorkflow(editedWorkflow);
        setEditMode(false);
        loadWorkflows();
      } else {
        toast.error('Erro ao guardar workflow');
      }
    } catch (error) {
      toast.error('Erro ao guardar workflow');
    } finally {
      setSaving(false);
    }
  };

  const toggleNotificacao = (key) => {
    setEditedWorkflow(prev => ({
      ...prev,
      notificacoes: {
        ...prev.notificacoes,
        [key]: !prev.notificacoes[key]
      }
    }));
  };

  const updateEstadoCor = (estadoId, novaCor) => {
    setEditedWorkflow(prev => ({
      ...prev,
      estados: prev.estados.map(e => 
        e.id === estadoId ? { ...e, cor: novaCor } : e
      )
    }));
  };

  const addDocObrigatorio = (estadoId, doc) => {
    if (!doc.trim()) return;
    setEditedWorkflow(prev => ({
      ...prev,
      estados: prev.estados.map(e =>
        e.id === estadoId
          ? { ...e, documentos_obrigatorios: [...(e.documentos_obrigatorios || []).filter(d => d !== doc), doc] }
          : e
      )
    }));
  };

  const removeDocObrigatorio = (estadoId, doc) => {
    setEditedWorkflow(prev => ({
      ...prev,
      estados: prev.estados.map(e =>
        e.id === estadoId
          ? { ...e, documentos_obrigatorios: (e.documentos_obrigatorios || []).filter(d => d !== doc) }
          : e
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflows-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Configuração de Workflows</h1>
          <p className="text-slate-500 dark:text-slate-400">Personalize os fluxos de trabalho da plataforma</p>
        </div>
        <Button onClick={loadWorkflows} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Workflows Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {workflows.map((workflow) => {
          const Icon = ENTIDADE_ICONS[workflow.entidade] || GitBranch;
          return (
            <Card 
              key={workflow.id} 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-[#0066CC]/30"
              onClick={() => openWorkflow(workflow)}
              data-testid={`workflow-card-${workflow.entidade}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{workflow.nome}</CardTitle>
                      <CardDescription>{workflow.descricao}</CardDescription>
                    </div>
                  </div>
                  <Badge className={workflow.ativo ? 'bg-green-500' : 'bg-slate-400'}>
                    {workflow.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* States Preview */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {workflow.estados?.map((estado) => (
                    <Badge 
                      key={estado.id} 
                      style={{ backgroundColor: estado.cor }}
                      className="text-white"
                    >
                      {estado.nome}
                    </Badge>
                  ))}
                </div>
                
                {/* Transitions Count */}
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>{workflow.estados?.length || 0} estados</span>
                  <span>{workflow.transicoes?.length || 0} transições</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Workflow Detail Modal */}
      <Dialog open={!!selectedWorkflow} onOpenChange={() => setSelectedWorkflow(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <GitBranch className="w-5 h-5 text-[#0066CC]" />
              {selectedWorkflow?.nome}
              {!editMode && (
                <Button variant="ghost" size="sm" onClick={() => setEditMode(true)}>
                  <Edit className="w-4 h-4" />
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedWorkflow?.descricao}
            </DialogDescription>
          </DialogHeader>

          {selectedWorkflow && (
            <div className="space-y-6">
              {/* States */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Estados do Workflow</Label>
                <Accordion type="multiple" className="space-y-1">
                  {(editMode ? editedWorkflow : selectedWorkflow).estados?.map((estado, idx) => (
                    <AccordionItem key={estado.id} value={estado.id} className="border rounded-lg overflow-hidden">
                      <AccordionTrigger className="px-3 py-2 hover:no-underline bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-center gap-3 w-full">
                          <span className="text-slate-400 font-mono text-sm w-5 text-center">{idx + 1}</span>
                          <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: estado.cor }}></div>
                          <span className="font-medium dark:text-white">{estado.nome}</span>
                          {estado.final && <Badge variant="outline" className="text-xs">Final</Badge>}
                          {estado.documentos_obrigatorios?.length > 0 && (
                            <Badge className="bg-amber-500 text-xs ml-auto mr-2">
                              {estado.documentos_obrigatorios.length} doc(s) obrigatório(s)
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3 pt-2 bg-white dark:bg-slate-900 border-t dark:border-slate-700">
                        {estado.descricao && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {estado.descricao}
                          </p>
                        )}
                        
                        {/* Documentos Obrigatórios */}
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            Documentos obrigatórios neste estado
                          </p>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {(estado.documentos_obrigatorios || []).length === 0 ? (
                              <span className="text-xs text-slate-400">Nenhum</span>
                            ) : (
                              (estado.documentos_obrigatorios || []).map(doc => (
                                <Badge key={doc} variant="outline" className="gap-1 text-xs bg-amber-50 border-amber-300">
                                  {doc}
                                  {editMode && (
                                    <button onClick={() => removeDocObrigatorio(estado.id, doc)} className="ml-1 text-red-500 hover:text-red-700">
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </Badge>
                              ))
                            )}
                          </div>
                          {editMode && (
                            <DocObrigatorioInput estadoId={estado.id} onAdd={addDocObrigatorio} />
                          )}
                        </div>

                        {/* Requisitos */}
                        {estado.requisitos?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                              Requisitos
                            </p>
                            <ul className="space-y-0.5">
                              {estado.requisitos.map((req, i) => (
                                <li key={i} className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                                  <span className="text-green-500 mt-0.5">•</span>{req}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {estado.acoes_automaticas?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-blue-500" />
                              Ações automáticas
                            </p>
                            <ul className="space-y-0.5">
                              {estado.acoes_automaticas.map((acao, i) => (
                                <li key={i} className="text-xs text-blue-600 dark:text-blue-400 flex items-start gap-1.5">
                                  <span className="mt-0.5">→</span>{acao}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {editMode && (
                          <div className="mt-3 pt-2 border-t dark:border-slate-700">
                            <Label className="text-xs text-slate-500">Cor do estado</Label>
                            <Input
                              type="color"
                              value={estado.cor}
                              onChange={(e) => updateEstadoCor(estado.id, e.target.value)}
                              className="w-12 h-8 p-1 cursor-pointer mt-1"
                            />
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Transitions */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Transições Permitidas</Label>
                <div className="space-y-2">
                  {(editMode ? editedWorkflow : selectedWorkflow).transicoes?.map((trans, idx) => {
                    const deEstado = selectedWorkflow.estados.find(e => e.id === trans.de);
                    const paraEstado = selectedWorkflow.estados.find(e => e.id === trans.para);
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <Badge style={{ backgroundColor: deEstado?.cor }} className="text-white">
                          {deEstado?.nome}
                        </Badge>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <Badge style={{ backgroundColor: paraEstado?.cor }} className="text-white">
                          {paraEstado?.nome}
                        </Badge>
                        <span className="text-sm text-slate-500 ml-auto">
                          Ação: <strong>{trans.acao}</strong>
                        </span>
                        {trans.requer_notas && (
                          <Badge variant="outline" className="text-xs">Notas</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notifications */}
              <div>
                <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Notificações
                </Label>
                <div className="space-y-3">
                  {Object.entries((editMode ? editedWorkflow : selectedWorkflow).notificacoes || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="text-sm dark:text-white">
                          {key === 'email_instituicao' && 'Enviar email para instituição'}
                          {key === 'email_voluntarios' && 'Enviar email para voluntários'}
                          {key === 'alerta_interno' && 'Criar alerta interno'}
                        </span>
                      </div>
                      {editMode ? (
                        <Switch
                          checked={value}
                          onCheckedChange={() => toggleNotificacao(key)}
                        />
                      ) : (
                        <Badge className={value ? 'bg-green-500' : 'bg-slate-400'}>
                          {value ? 'Sim' : 'Não'}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Workflow Status */}
              {editMode && (
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Workflow Ativo</Label>
                    <p className="text-xs text-slate-500">Desativar irá manter o workflow visível mas não funcional</p>
                  </div>
                  <Switch
                    checked={editedWorkflow.ativo}
                    onCheckedChange={(checked) => setEditedWorkflow(prev => ({ ...prev, ativo: checked }))}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => {
                  setEditedWorkflow(JSON.parse(JSON.stringify(selectedWorkflow)));
                  setEditMode(false);
                }}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </>
            ) : (
              <div className="flex gap-2 w-full justify-between">
                {selectedWorkflow?.entidade === 'candidaturas' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={async () => {
                      const token = localStorage.getItem('admin_token');
                      try {
                        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/workflows/reset-candidaturas`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        toast.success('Workflow de candidaturas reposto para os estados do 4º Steering');
                        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/workflows/entidade/candidaturas`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (resp.ok) setSelectedWorkflow(await resp.json());
                      } catch (e) { toast.error('Erro ao repor workflow'); }
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Repor workflow inicial
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelectedWorkflow(null)} className="ml-auto">Fechar</Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
